import React, { useEffect, useState } from 'react';
import { qualityAPI, inventoryAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { EntityLink } from '@/components/EntityLink';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  ShieldCheck, Search, Clock, CheckCircle2, AlertTriangle,
  ChevronLeft, ChevronRight, Loader2, PackageCheck, Ban, Truck, Package, Replace,
} from 'lucide-react';
import { toast } from 'sonner';

const QC_STATUS_COLORS = {
  pending_qc: 'bg-amber-50 text-amber-700 border-amber-200',
  qc_completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export default function QualityCheckPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [mainTab, setMainTab] = useState('inbound'); // 'inbound', 'outbound', or 'receipt'
  const [activeTab, setActiveTab] = useState('pending');
  const [challans, setChallans] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ pending_qc: 0, qc_completed: 0, quarantined_items: 0, rejected_items: 0, receipt_qc_pending: 0 });
  const limit = 25;

  // Outbound QC state
  const [outboundChallans, setOutboundChallans] = useState([]);
  const [outboundTotal, setOutboundTotal] = useState(0);
  const [outboundPage, setOutboundPage] = useState(1);
  const [outboundSearch, setOutboundSearch] = useState('');
  const [outboundLoading, setOutboundLoading] = useState(false);
  const [outboundTab, setOutboundTab] = useState('pending');

  // Receipt QC state
  const [receiptItems, setReceiptItems] = useState([]);
  const [receiptTotal, setReceiptTotal] = useState(0);
  const [receiptPage, setReceiptPage] = useState(1);
  const [receiptSearch, setReceiptSearch] = useState('');
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptTab, setReceiptTab] = useState('pending');

  // Admin receipt corrections (wrong item booked / void receipt)
  const [correctTarget, setCorrectTarget] = useState(null);
  const [voidTarget, setVoidTarget] = useState(null);
  const [fixItems, setFixItems] = useState([]);
  const [fixBusy, setFixBusy] = useState(false);

  // Receipt QC Decision dialog
  const [receiptQCOpen, setReceiptQCOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [receiptDisposition, setReceiptDisposition] = useState({ accepted_qty: '', quarantined_qty: '', rejected_qty: '', remarks: '' });
  const [submittingReceipt, setSubmittingReceipt] = useState(false);

  // QC Decision dialog
  const [qcOpen, setQcOpen] = useState(false);
  const [selectedChallan, setSelectedChallan] = useState(null);
  const [dispositions, setDispositions] = useState([]);
  const [overallRemarks, setOverallRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isOutboundQC, setIsOutboundQC] = useState(false);

  // Receive for QC state
  const [receivingForQC, setReceivingForQC] = useState(null);

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { 
    if (mainTab === 'inbound') {
      loadChallans(); 
    }
  }, [page, activeTab, search, mainTab]);
  
  useEffect(() => {
    if (mainTab === 'outbound') {
      loadOutboundChallans();
    }
  }, [outboundPage, outboundTab, outboundSearch, mainTab]);

  useEffect(() => {
    if (mainTab === 'receipt') {
      loadReceiptItems();
    }
  }, [receiptPage, receiptTab, receiptSearch, mainTab]);

  const loadStats = async () => {
    try {
      const res = await qualityAPI.dashboard();
      setStats(res.data);
    } catch { /* silent */ }
  };

  const loadChallans = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search.trim()) params.search = search.trim();
      const res = activeTab === 'pending'
        ? await qualityAPI.listPending(params)
        : await qualityAPI.listHistory(params);
      setChallans(res.data.challans || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load QC data'); }
    finally { setLoading(false); }
  };

  // Admin: close inbound QC for material already returned to the customer
  const [closeQCDlg, setCloseQCDlg] = useState(null);
  const [closeQCSaving, setCloseQCSaving] = useState(false);
  const submitCloseQC = async () => {
    if (!closeQCDlg) return;
    setCloseQCSaving(true);
    try {
      const res = await qualityAPI.closeInboundQC(closeQCDlg.challan.id, { reason: closeQCDlg.reason.trim() });
      toast.success(res.data.message || 'QC closed');
      setCloseQCDlg(null);
      loadChallans();
      loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to close QC'); }
    finally { setCloseQCSaving(false); }
  };

  const loadOutboundChallans = async () => {
    setOutboundLoading(true);
    try {
      const params = { page: outboundPage, limit };
      if (outboundSearch.trim()) params.search = outboundSearch.trim();
      const res = outboundTab === 'pending'
        ? await qualityAPI.listOutboundPending(params)
        : await qualityAPI.listOutboundHistory(params);
      setOutboundChallans(res.data.challans || []);
      setOutboundTotal(res.data.total || 0);
    } catch { toast.error('Failed to load outbound QC data'); }
    finally { setOutboundLoading(false); }
  };

  const loadReceiptItems = async () => {
    setReceiptLoading(true);
    try {
      const params = { page: receiptPage, limit };
      if (receiptSearch.trim()) params.search = receiptSearch.trim();
      const res = receiptTab === 'pending'
        ? await qualityAPI.listReceiptPending(params)
        : await qualityAPI.listReceiptHistory(params);
      setReceiptItems(res.data.transactions || []);
      setReceiptTotal(res.data.total || 0);
    } catch { toast.error('Failed to load receipt QC data'); }
    finally { setReceiptLoading(false); }
  };

  const [cleaningJW, setCleaningJW] = useState(false);
  const handleCleanupJobworkReceiptQC = async () => {
    // Step 1: dry-run preview
    let preview;
    try {
      const res = await qualityAPI.cleanupJobworkReceiptQC({ dry_run: true });
      preview = res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to preview cleanup');
      return;
    }
    const n = preview?.would_clean ?? 0;
    if (!n) {
      toast.success('Nothing to clean — no Job Work items in Receipt QC');
      return;
    }
    if (!window.confirm(
      `Found ${n} Job-Work-Item entr${n === 1 ? 'y' : 'ies'} in Receipt QC.\n\n`
      + 'These will be removed from the Receipt QC queue (Inbound QC remains the source of truth for Job Work items). '
      + 'Inventory balances are NOT touched.\n\nProceed?'
    )) return;

    setCleaningJW(true);
    try {
      const res = await qualityAPI.cleanupJobworkReceiptQC();
      toast.success(`Cleaned ${res.data.cleaned} Receipt-QC entr${res.data.cleaned === 1 ? 'y' : 'ies'}`);
      loadReceiptItems();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleaningJW(false);
    }
  };

  const handleReceiveForQC = async (challan) => {
    // This function is deprecated - we now use the dialog flow
    toast.info('Please use the Receive & QC dialog for partial receiving');
  };

  const openQCDialog = (challan, isOutbound = false) => {
    setSelectedChallan(challan);
    setIsOutboundQC(isOutbound);
    
    if (isOutbound) {
      // For outbound: initialize with pending quantities to receive
      const items = (challan.items || []).map(item => ({
        item_id: item.item_id,
        item_name: item.item_name,
        item_unique_id: item.item_unique_id,
        sent_qty: item.sent_qty || item.quantity || 0,
        already_received: item.received_qty || 0,
        pending_receive_qty: item.pending_receive_qty || (item.quantity - (item.received_qty || 0)) || 0,
        received_qty: '', // qty to receive now
        accepted_qty: '',
        quarantined_qty: '',
        rejected_qty: '',
        remarks: '',
      }));
      setDispositions(items);
    } else {
      // For inbound: QC flow with partial inspection support
      const items = (challan.items || []).map((item, idx) => ({
        item_id: item.item_id,
        line_index: idx,
        item_name: item.item_name,
        item_unique_id: item.item_unique_id,
        total_qty: item.quantity,
        already_inspected: item.qc_inspected_qty || 0,
        pending_qc_qty: item.pending_qc_qty ?? (item.quantity - (item.qc_inspected_qty || 0)),
        prev_accepted: item.qc_accepted_qty || 0,
        prev_quarantined: item.qc_quarantined_qty || 0,
        prev_rejected: item.qc_rejected_qty || 0,
        quality_status: item.quality_status || 'pending_qc',
        traceability_id: item.traceability_id || '',
        work_order_number: item.work_order_number || '',
        accepted_qty: '',
        quarantined_qty: '',
        rejected_qty: '',
        remarks: '',
      }));
      setDispositions(items);
    }
    setOverallRemarks('');
    setQcOpen(true);
  };

  const updateDisposition = (idx, field, value) => {
    setDispositions(prev => prev.map((d, i) => i === idx ? { ...d, [field]: value } : d));
  };

  // Quick-set: accept all qty for an item (uses pending_qc_qty for partial QC)
  const acceptAll = (idx) => {
    setDispositions(prev => prev.map((d, i) => {
      if (i !== idx) return d;
      if (isOutboundQC) {
        const qty = String(d.pending_receive_qty);
        return { ...d, received_qty: qty, accepted_qty: qty, quarantined_qty: '0', rejected_qty: '0' };
      }
      // For inbound: use pending_qc_qty (remaining to inspect)
      const pendingQty = d.pending_qc_qty ?? d.total_qty;
      return { ...d, accepted_qty: String(pendingQty), quarantined_qty: '0', rejected_qty: '0' };
    }));
  };

  // Quick-set: accept all for entire challan (uses pending_qc_qty for partial QC)
  const acceptAllItems = () => {
    setDispositions(prev => prev.map(d => {
      if (isOutboundQC) {
        const qty = String(d.pending_receive_qty);
        return { ...d, received_qty: qty, accepted_qty: qty, quarantined_qty: '0', rejected_qty: '0' };
      }
      // For inbound: use pending_qc_qty (remaining to inspect)
      const pendingQty = d.pending_qc_qty ?? d.total_qty;
      return { ...d, accepted_qty: String(pendingQty), quarantined_qty: '0', rejected_qty: '0' };
    }));
  };

  // Auto-fill accepted_qty when received_qty changes (for outbound)
  const handleReceivedQtyChange = (idx, value) => {
    setDispositions(prev => prev.map((d, i) => {
      if (i !== idx) return d;
      // Auto-set accepted_qty = received_qty for convenience
      return { ...d, received_qty: value, accepted_qty: value, quarantined_qty: '0', rejected_qty: '0' };
    }));
  };

  // Load the item list lazily for the "wrong item" correction dialog.
  useEffect(() => {
    if (correctTarget && fixItems.length === 0) {
      inventoryAPI.listItems({ limit: 2000, sort_by: 'item_name', sort_order: 'asc' })
        .then(r => setFixItems(r.data?.items || r.data || [])).catch(() => {});
    }
  }, [correctTarget, fixItems.length]);

  // ===== Receipt QC Handlers =====
  const openReceiptQCDialog = (txn) => {
    setSelectedReceipt(txn);
    const pending = txn.pending_qc_qty ?? (txn.quantity - (txn.qc_inspected_qty || 0));
    setReceiptDisposition({ accepted_qty: '', quarantined_qty: '', rejected_qty: '', remarks: '', pending_qty: pending });
    setReceiptQCOpen(true);
  };

  const receiptAcceptAll = () => {
    setReceiptDisposition(prev => ({ ...prev, accepted_qty: String(prev.pending_qty), quarantined_qty: '0', rejected_qty: '0' }));
  };

  const handleSubmitReceiptQC = async () => {
    const accepted = parseFloat(receiptDisposition.accepted_qty) || 0;
    const quarantined = parseFloat(receiptDisposition.quarantined_qty) || 0;
    const rejected = parseFloat(receiptDisposition.rejected_qty) || 0;
    const total_disposed = accepted + quarantined + rejected;
    
    if (total_disposed === 0) {
      toast.error('Enter at least one disposition quantity');
      return;
    }
    if (total_disposed > receiptDisposition.pending_qty) {
      toast.error(`Total disposition (${total_disposed}) exceeds pending QC qty (${receiptDisposition.pending_qty})`);
      return;
    }

    setSubmittingReceipt(true);
    try {
      await qualityAPI.receiptDecide({
        transaction_id: selectedReceipt.id,
        accepted_qty: accepted,
        quarantined_qty: quarantined,
        rejected_qty: rejected,
        remarks: receiptDisposition.remarks,
      });
      toast.success('Receipt QC decision recorded. Stock moved to respective godowns.');
      setReceiptQCOpen(false);
      loadReceiptItems();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process receipt QC');
    } finally { setSubmittingReceipt(false); }
  };

  const submitItemCorrection = async () => {
    if (!correctTarget?.item_id) { toast.error('Select the correct item'); return; }
    if (!correctTarget.reason.trim()) { toast.error('Enter a reason for the audit log'); return; }
    setFixBusy(true);
    try {
      const res = await qualityAPI.correctReceiptItem({
        transaction_id: correctTarget.txn.id,
        new_item_id: correctTarget.item_id,
        reason: correctTarget.reason.trim(),
      });
      toast.success(res.data.message, { duration: 8000 });
      setCorrectTarget(null);
      loadReceiptItems();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not correct the item', { duration: 10000 });
    } finally { setFixBusy(false); }
  };

  const submitVoidReceipt = async () => {
    if (!voidTarget?.reason.trim()) { toast.error('Enter a reason for the audit log'); return; }
    setFixBusy(true);
    try {
      const res = await qualityAPI.voidReceipt({
        transaction_id: voidTarget.txn.id,
        reason: voidTarget.reason.trim(),
        stock_already_adjusted: voidTarget.stock_already_adjusted,
      });
      toast.success(res.data.message, { duration: 8000 });
      setVoidTarget(null);
      loadReceiptItems();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not void the receipt', { duration: 10000 });
    } finally { setFixBusy(false); }
  };

  const handleSubmitQC = async () => {
    if (isOutboundQC) {
      // Outbound: Receive + QC in one action
      const itemsToProcess = dispositions.filter(d => parseFloat(d.received_qty) > 0);
      
      if (itemsToProcess.length === 0) {
        toast.error('Please enter quantities to receive');
        return;
      }

      // Validate each item
      for (const d of itemsToProcess) {
        const received = parseFloat(d.received_qty) || 0;
        const accepted = parseFloat(d.accepted_qty) || 0;
        const quarantined = parseFloat(d.quarantined_qty) || 0;
        const rejected = parseFloat(d.rejected_qty) || 0;
        const qcTotal = accepted + quarantined + rejected;

        if (received > d.pending_receive_qty) {
          toast.error(`Received qty (${received}) exceeds pending qty (${d.pending_receive_qty}) for ${d.item_name}`);
          return;
        }
        if (qcTotal !== received) {
          toast.error(`QC disposition (${qcTotal}) must equal received qty (${received}) for ${d.item_name}`);
          return;
        }
      }

      setSubmitting(true);
      try {
        await qualityAPI.receiveAndQC({
          challan_id: selectedChallan.id,
          items: itemsToProcess.map(d => ({
            item_id: d.item_id,
            received_qty: parseFloat(d.received_qty) || 0,
            accepted_qty: parseFloat(d.accepted_qty) || 0,
            quarantined_qty: parseFloat(d.quarantined_qty) || 0,
            rejected_qty: parseFloat(d.rejected_qty) || 0,
            remarks: d.remarks,
          })),
          overall_remarks: overallRemarks,
        });
        toast.success('Items received and QC completed. Stock moved to respective godowns.');
        loadOutboundChallans();
        setQcOpen(false);
        loadStats();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to process receive and QC');
      } finally { setSubmitting(false); }
    } else {
      // Inbound: QC flow with partial inspection support
      // Filter items with non-zero disposition
      const itemsToProcess = dispositions.filter(d => {
        const accepted = parseFloat(d.accepted_qty) || 0;
        const quarantined = parseFloat(d.quarantined_qty) || 0;
        const rejected = parseFloat(d.rejected_qty) || 0;
        return (accepted + quarantined + rejected) > 0;
      });

      if (itemsToProcess.length === 0) {
        toast.error('Please enter disposition quantities for at least one item');
        return;
      }

      // Validate each item's disposition doesn't exceed pending qty
      for (const d of itemsToProcess) {
        const accepted = parseFloat(d.accepted_qty) || 0;
        const quarantined = parseFloat(d.quarantined_qty) || 0;
        const rejected = parseFloat(d.rejected_qty) || 0;
        const disposed = accepted + quarantined + rejected;
        const pendingQty = d.pending_qc_qty ?? d.total_qty;
        
        if (disposed > pendingQty) {
          toast.error(`Total disposition (${disposed}) exceeds pending QC qty (${pendingQty}) for ${d.item_name}`);
          return;
        }
      }

      setSubmitting(true);
      try {
        const res = await qualityAPI.decide({
          challan_id: selectedChallan.id,
          items: itemsToProcess.map(d => ({
            item_id: d.item_id,
            line_index: d.line_index !== undefined ? d.line_index : -1,
            accepted_qty: parseFloat(d.accepted_qty) || 0,
            quarantined_qty: parseFloat(d.quarantined_qty) || 0,
            rejected_qty: parseFloat(d.rejected_qty) || 0,
            remarks: d.remarks,
          })),
          overall_remarks: overallRemarks,
          is_partial: true,
        });
        
        // Check if all items are fully inspected
        const hasPartial = res.data?.results?.some(r => r.pending_qty > 0);
        if (hasPartial) {
          toast.success('Partial QC recorded. Some items still pending inspection.');
        } else {
          toast.success('QC decision recorded. Stock moved to respective godowns.');
        }
        loadChallans();
        setQcOpen(false);
        loadStats();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to process QC decision');
      } finally { setSubmitting(false); }
    }
  };

  const totalPages = Math.ceil(total / limit);
  const outboundTotalPages = Math.ceil(outboundTotal / limit);
  const receiptTotalPages = Math.ceil(receiptTotal / limit);

  return (
    <div className="space-y-4" data-testid="quality-check-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Quality Check</h1>
          <p className="text-xs text-muted-foreground">Inspect inbound and outbound challans and assign dispositions</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <div>
                <p className="text-lg font-bold text-amber-700" data-testid="stat-pending">{stats.pending_qc}</p>
                <p className="text-[10px] text-amber-600">Pending QC</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <div>
                <p className="text-lg font-bold text-emerald-700" data-testid="stat-completed">{stats.qc_completed}</p>
                <p className="text-[10px] text-emerald-600">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-orange-600" />
              <div>
                <p className="text-lg font-bold text-orange-700" data-testid="stat-quarantined">{stats.quarantined_items}</p>
                <p className="text-[10px] text-orange-600">Quarantined</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Ban className="w-4 h-4 text-red-600" />
              <div>
                <p className="text-lg font-bold text-red-700" data-testid="stat-rejected">{stats.rejected_items}</p>
                <p className="text-[10px] text-red-600">Rejected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs: Inbound vs Outbound vs Receipt */}
      <Tabs value={mainTab} onValueChange={v => { setMainTab(v); setPage(1); setOutboundPage(1); setReceiptPage(1); }}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="inbound" data-testid="main-tab-inbound" className="flex items-center gap-1.5">
            <Package className="w-3.5 h-3.5" /> Inbound QC
          </TabsTrigger>
          <TabsTrigger value="outbound" data-testid="main-tab-outbound" className="flex items-center gap-1.5">
            <Truck className="w-3.5 h-3.5" /> Outbound QC
          </TabsTrigger>
          <TabsTrigger value="receipt" data-testid="main-tab-receipt" className="flex items-center gap-1.5">
            <PackageCheck className="w-3.5 h-3.5" /> Receipt QC
            {stats.receipt_qc_pending > 0 && (
              <Badge variant="destructive" className="ml-1 h-4 min-w-[18px] text-[10px] px-1">{stats.receipt_qc_pending}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Inbound QC Tab */}
        <TabsContent value="inbound" className="mt-4">
          <Tabs value={activeTab} onValueChange={v => { setActiveTab(v); setPage(1); }}>
            <TabsList>
              <TabsTrigger value="pending" data-testid="tab-pending">
                <Clock className="w-3.5 h-3.5 mr-1" /> Pending ({stats.pending_qc})
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="tab-history">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> History
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-4 space-y-3">
              <Card>
                <CardContent className="p-3">
                  <div className="relative max-w-xs">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search challan, item, supplier..."
                      value={search}
                      onChange={e => { setSearch(e.target.value); setPage(1); }}
                      className="h-8 text-xs pl-7"
                      data-testid="qc-search"
                    />
                  </div>
                </CardContent>
              </Card>

              {loading ? (
                <Card><CardContent className="p-8 text-center text-xs text-muted-foreground">Loading...</CardContent></Card>
              ) : challans.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <p className="text-sm text-muted-foreground">
                      {activeTab === 'pending' ? 'No challans pending quality check' : 'No QC history yet'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {challans.map(challan => (
                    <Card key={challan.id} data-testid={`qc-challan-${challan.challan_number}`}>
                      <CardHeader className="p-3 pb-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-sm font-semibold">{challan.challan_number}</CardTitle>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              From: {challan.supplier_name || '-'} |
                              Received: {challan.received_date ? new Date(challan.received_date).toLocaleDateString() : challan.created_at?.slice(0, 10)} |
                              Items: {(challan.items || []).length}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`text-[10px] ${QC_STATUS_COLORS[challan.quality_status] || ''}`}>
                              {(challan.quality_status || 'pending_qc').replace(/_/g, ' ')}
                            </Badge>
                            {activeTab === 'pending' && (
                              <Button
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => openQCDialog(challan, false)}
                                data-testid={`qc-inspect-${challan.challan_number}`}
                              >
                                <PackageCheck className="w-3.5 h-3.5 mr-1" /> Inspect
                              </Button>
                            )}
                            {activeTab === 'pending' && isAdmin && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs border-slate-300"
                                onClick={() => setCloseQCDlg({ challan, reason: '' })}
                                title="Close QC for material already returned to the customer"
                                data-testid={`qc-close-returned-${challan.challan_number}`}
                              >
                                <Ban className="w-3.5 h-3.5 mr-1" /> Close Inbound QC
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-3 pt-0">
                        <table className="w-full text-xs" data-testid={`qc-items-table-${challan.challan_number}`}>
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="text-left p-2 font-medium text-muted-foreground">Item</th>
                              <th className="text-center p-2 font-medium text-muted-foreground">Qty</th>
                              <th className="text-center p-2 font-medium text-muted-foreground">UOM</th>
                              {activeTab === 'history' && (
                                <>
                                  <th className="text-center p-2 font-medium text-muted-foreground text-emerald-600">Accepted</th>
                                  <th className="text-center p-2 font-medium text-muted-foreground text-orange-600">Quarantined</th>
                                  <th className="text-center p-2 font-medium text-muted-foreground text-red-600">Rejected</th>
                                </>
                              )}
                              <th className="text-center p-2 font-medium text-muted-foreground">Location</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(challan.items || []).map((item, idx) => (
                              <tr key={idx} className="border-b last:border-0">
                                <td className="p-2">
                                  <p className="font-medium"><EntityLink type="item" id={item.item_id}>{item.item_name}</EntityLink></p>
                                  <div className="flex gap-2">
                                    <p className="text-[10px] text-muted-foreground font-mono"><EntityLink type="item" id={item.item_id} className="font-mono text-[10px] text-muted-foreground">{item.item_unique_id}</EntityLink></p>
                                    {item.traceability_id && (
                                      <span className="text-[10px] text-blue-600 font-mono">Trace: {item.traceability_id}</span>
                                    )}
                                  </div>
                                </td>
                                <td className="p-2 text-center font-medium">{item.quantity}</td>
                                <td className="p-2 text-center text-muted-foreground">{item.unit_of_measure || 'Nos'}</td>
                                {activeTab === 'history' && (
                                  <>
                                    <td className="p-2 text-center text-emerald-600 font-medium">{item.qc_accepted_qty || 0}</td>
                                    <td className="p-2 text-center text-orange-600 font-medium">{item.qc_quarantined_qty || 0}</td>
                                    <td className="p-2 text-center text-red-600 font-medium">{item.qc_rejected_qty || 0}</td>
                                  </>
                                )}
                                <td className="p-2 text-center">
                                  <span className="text-[10px]">{item.staging_godown_name || 'Inbound Staging'}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {activeTab === 'history' && challan.qc_decided_by_name && (
                          <p className="text-[10px] text-muted-foreground mt-2 pt-2 border-t">
                            QC by: {challan.qc_decided_by_name} on {challan.qc_decided_at ? new Date(challan.qc_decided_at).toLocaleDateString() : '-'}
                            {challan.qc_overall_remarks && <> | Remarks: {challan.qc_overall_remarks}</>}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-7 text-xs">
                          <ChevronLeft className="w-3 h-3 mr-1" /> Prev
                        </Button>
                        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-7 text-xs">
                          Next <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Outbound QC Tab (Supplier Returns from Job Work) */}
        <TabsContent value="outbound" className="mt-4">
          <Card className="mb-4 bg-blue-50 border-blue-200">
            <CardContent className="p-3">
              <p className="text-xs text-blue-700">
                <strong>Outbound QC:</strong> Receive and inspect materials returned from <strong>suppliers</strong> after processing (job work). 
                Suppliers may return items in batches. For each batch, receive the quantity and assign QC disposition (Accept/Quarantine/Reject).
              </p>
            </CardContent>
          </Card>

          <Tabs value={outboundTab} onValueChange={v => { setOutboundTab(v); setOutboundPage(1); }}>
            <TabsList>
              <TabsTrigger value="pending" data-testid="outbound-tab-pending">
                <Clock className="w-3.5 h-3.5 mr-1" /> Pending Receipt
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="outbound-tab-history">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Fully Received
              </TabsTrigger>
            </TabsList>

            <TabsContent value={outboundTab} className="mt-4 space-y-3">
              <Card>
                <CardContent className="p-3">
                  <div className="relative max-w-xs">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search challan, item, supplier..."
                      value={outboundSearch}
                      onChange={e => { setOutboundSearch(e.target.value); setOutboundPage(1); }}
                      className="h-8 text-xs pl-7"
                      data-testid="outbound-qc-search"
                    />
                  </div>
                </CardContent>
              </Card>

              {outboundLoading ? (
                <Card><CardContent className="p-8 text-center text-xs text-muted-foreground">Loading...</CardContent></Card>
              ) : outboundChallans.length === 0 ? (
                <Card>
                  <CardContent className="p-8 text-center">
                    <p className="text-sm text-muted-foreground">
                      {outboundTab === 'pending' ? 'No outbound challans with pending items' : 'No fully received challans yet'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {outboundChallans.map(challan => (
                    <Card key={challan.id} data-testid={`outbound-qc-challan-${challan.challan_number}`}>
                      <CardHeader className="p-3 pb-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-sm font-semibold">{challan.challan_number}</CardTitle>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              Supplier: {challan.supplier_name || '-'} |
                              Sent: {challan.created_at?.slice(0, 10)} |
                              Items: {(challan.items || []).length}
                              {challan.receipts_count > 0 && <> | Receipts: {challan.receipts_count}</>}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`text-[10px] ${
                              challan.status === 'fully_received' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              challan.status === 'partially_received' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                              'bg-slate-50 text-slate-700 border-slate-200'
                            }`}>
                              {(challan.status || 'sent').replace(/_/g, ' ')}
                            </Badge>
                            {outboundTab === 'pending' && challan.has_pending_receive && (
                              <Button
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => openQCDialog(challan, true)}
                                data-testid={`outbound-qc-receive-${challan.challan_number}`}
                              >
                                <PackageCheck className="w-3.5 h-3.5 mr-1" /> Receive & QC
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-3 pt-0">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="text-left p-2 font-medium text-muted-foreground">Item</th>
                              <th className="text-center p-2 font-medium text-muted-foreground">Sent</th>
                              <th className="text-center p-2 font-medium text-muted-foreground">Received</th>
                              <th className="text-center p-2 font-medium text-muted-foreground text-amber-600">Pending</th>
                              <th className="text-center p-2 font-medium text-muted-foreground text-emerald-600">Accepted</th>
                              <th className="text-center p-2 font-medium text-muted-foreground text-orange-600">Quarantine</th>
                              <th className="text-center p-2 font-medium text-muted-foreground text-red-600">Rejected</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(challan.items || []).map((item, idx) => (
                              <tr key={idx} className="border-b last:border-0">
                                <td className="p-2">
                                  <p className="font-medium"><EntityLink type="item" id={item.item_id}>{item.item_name}</EntityLink></p>
                                  <p className="text-[10px] text-muted-foreground font-mono"><EntityLink type="item" id={item.item_id} className="font-mono text-[10px] text-muted-foreground">{item.item_unique_id}</EntityLink></p>
                                </td>
                                <td className="p-2 text-center font-medium">{item.sent_qty || item.quantity}</td>
                                <td className="p-2 text-center">{item.received_qty || 0}</td>
                                <td className="p-2 text-center text-amber-600 font-medium">{item.pending_receive_qty || (item.quantity - (item.received_qty || 0))}</td>
                                <td className="p-2 text-center text-emerald-600">{item.qc_accepted_qty || 0}</td>
                                <td className="p-2 text-center text-orange-600">{item.qc_quarantined_qty || 0}</td>
                                <td className="p-2 text-center text-red-600">{item.qc_rejected_qty || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </CardContent>
                    </Card>
                  ))}

                  {outboundTotalPages > 1 && (
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">Page {outboundPage} of {outboundTotalPages}</p>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" disabled={outboundPage <= 1} onClick={() => setOutboundPage(p => p - 1)} className="h-7 text-xs">
                          <ChevronLeft className="w-3 h-3 mr-1" /> Prev
                        </Button>
                        <Button size="sm" variant="outline" disabled={outboundPage >= outboundTotalPages} onClick={() => setOutboundPage(p => p + 1)} className="h-7 text-xs">
                          Next <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Receipt QC Tab (Purchase/Direct Receive items with QC flag) */}
        <TabsContent value="receipt" className="mt-4">
          <Card className="mb-4 bg-indigo-50 border-indigo-200">
            <CardContent className="p-3">
              <div className="flex items-start justify-between gap-3">
                <p className="text-xs text-indigo-700 flex-1">
                  <strong>Receipt QC:</strong> Items received via Purchase Orders or direct entry with <strong>"Perform QC on Receipt"</strong> enabled.
                  These items are held in <strong>Inbound Staging</strong> until QC disposition is assigned (Accept → Stores, Quarantine, or Reject).
                  Job Work items are NOT included here — they are quality-checked through the <strong>Inbound QC</strong> flow.
                </p>
                {isAdmin && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleCleanupJobworkReceiptQC}
                    disabled={cleaningJW}
                    className="h-7 text-[11px] shrink-0 bg-white"
                    data-testid="cleanup-jobwork-receipt-qc-btn"
                    title="Remove legacy Job-Work-Item entries from this queue. Inventory is not touched."
                  >
                    {cleaningJW ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Ban className="w-3 h-3 mr-1" />}
                    {cleaningJW ? 'Cleaning…' : 'Clean up Job Work entries'}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Tabs value={receiptTab} onValueChange={v => { setReceiptTab(v); setReceiptPage(1); }}>
            <TabsList>
              <TabsTrigger value="pending" data-testid="receipt-tab-pending">
                <Clock className="w-3.5 h-3.5 mr-1" /> Pending QC
                {stats.receipt_qc_pending > 0 && (
                  <Badge variant="destructive" className="ml-1 h-4 min-w-[18px] text-[10px] px-1">{stats.receipt_qc_pending}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="receipt-tab-history">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Completed
              </TabsTrigger>
            </TabsList>

            <TabsContent value={receiptTab} className="mt-4 space-y-3">
              <Card>
                <CardContent className="p-3">
                  <div className="relative max-w-xs">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search item, reference..."
                      value={receiptSearch}
                      onChange={e => { setReceiptSearch(e.target.value); setReceiptPage(1); }}
                      className="h-8 text-xs pl-7"
                      data-testid="receipt-qc-search"
                    />
                  </div>
                </CardContent>
              </Card>

              {receiptLoading ? (
                <Card><CardContent className="p-8 text-center text-xs text-muted-foreground">Loading...</CardContent></Card>
              ) : receiptItems.length === 0 ? (
                <Card><CardContent className="p-8 text-center text-xs text-muted-foreground">No {receiptTab === 'pending' ? 'pending' : 'completed'} receipt QC items found.</CardContent></Card>
              ) : (
                <div className="space-y-3">
                  {receiptItems.map((txn) => (
                    <Card key={txn.id} className="hover:shadow-sm transition-shadow" data-testid={`receipt-qc-item-${txn.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <p className="font-medium text-sm">
                                <EntityLink type="item" id={txn.item_id}>{txn.item_name}</EntityLink>
                              </p>
                              <Badge variant="outline" className={`text-[10px] ${
                                txn.qc_status === 'qc_completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                txn.qc_status === 'partially_inspected' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                'bg-amber-50 text-amber-700 border-amber-200'
                              }`}>
                                {txn.qc_status === 'qc_completed' ? 'Completed' : txn.qc_status === 'partially_inspected' ? 'Partial' : 'Pending'}
                              </Badge>
                            </div>
                            <div className="flex gap-3 flex-wrap text-[10px] text-muted-foreground">
                              {txn.reference_number && <span>Ref: {txn.reference_number}</span>}
                              {txn.qc_supplier_name && <span>Supplier: {txn.qc_supplier_name}</span>}
                              <span>Godown: {txn.godown_name || 'Inbound Staging'}</span>
                              <span>Date: {txn.date}</span>
                            </div>
                            <div className="flex gap-3 mt-1.5 flex-wrap">
                              <Badge variant="outline" className="text-xs">Total: {txn.quantity} {txn.unit_of_measure || ''}</Badge>
                              {txn.qc_inspected_qty > 0 && (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                  Inspected: {txn.qc_inspected_qty}
                                </Badge>
                              )}
                              <Badge variant="outline" className={`text-xs ${
                                (txn.pending_qc_qty || 0) > 0 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              }`}>
                                Pending: {txn.pending_qc_qty ?? (txn.quantity - (txn.qc_inspected_qty || 0))}
                              </Badge>
                            </div>
                            {/* Show QC results for completed/partial */}
                            {txn.qc_inspected_qty > 0 && (
                              <div className="mt-1.5 text-[10px] flex gap-3 flex-wrap">
                                <span className="text-emerald-600">Accepted: {txn.qc_accepted_qty || 0}</span>
                                {(txn.qc_quarantined_qty || 0) > 0 && <span className="text-orange-600">Quarantined: {txn.qc_quarantined_qty}</span>}
                                {(txn.qc_rejected_qty || 0) > 0 && <span className="text-red-600">Rejected: {txn.qc_rejected_qty}</span>}
                              </div>
                            )}
                            {txn.qc_decided_by_name && (
                              <p className="text-[10px] text-muted-foreground mt-1">
                                QC by: {txn.qc_decided_by_name} {txn.qc_decided_at ? `on ${new Date(txn.qc_decided_at).toLocaleDateString()}` : ''}
                              </p>
                            )}
                          </div>
                          {receiptTab === 'pending' && txn.qc_status !== 'qc_completed' && (
                            <div className="flex flex-col gap-1 shrink-0">
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 h-8 text-xs"
                              onClick={() => openReceiptQCDialog(txn)}
                              data-testid={`receipt-qc-decide-${txn.id}`}
                            >
                              <ShieldCheck className="w-3.5 h-3.5 mr-1" /> QC Inspect
                            </Button>
                            {isAdmin && !(txn.qc_inspected_qty > 0) && (
                              <>
                                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                  onClick={() => setCorrectTarget({ txn, item_id: '', reason: '' })}
                                  data-testid={`receipt-correct-item-${txn.id}`}>
                                  <Replace className="w-3 h-3 mr-1" /> Wrong item?
                                </Button>
                                <Button size="sm" variant="outline"
                                  className="h-7 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50"
                                  onClick={() => setVoidTarget({ txn, reason: '', stock_already_adjusted: false })}
                                  data-testid={`receipt-void-${txn.id}`}>
                                  <Ban className="w-3 h-3 mr-1" /> Void receipt
                                </Button>
                              </>
                            )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {receiptTotalPages > 1 && (
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">Page {receiptPage} of {receiptTotalPages}</p>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" disabled={receiptPage <= 1} onClick={() => setReceiptPage(p => p - 1)} className="h-7 text-xs">
                          <ChevronLeft className="w-3 h-3 mr-1" /> Prev
                        </Button>
                        <Button size="sm" variant="outline" disabled={receiptPage >= receiptTotalPages} onClick={() => setReceiptPage(p => p + 1)} className="h-7 text-xs">
                          Next <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      {/* QC Decision Dialog */}
      {/* Admin: Close Inbound QC (material returned to customer) */}
      <Dialog open={!!closeQCDlg} onOpenChange={(o) => !o && setCloseQCDlg(null)}>
        <DialogContent className="max-w-md" data-testid="close-qc-dialog">
          <DialogHeader>
            <DialogTitle>Close Inbound QC — Material Returned</DialogTitle>
            <DialogDescription className="text-xs">
              Challan {closeQCDlg?.challan?.challan_number}. This closes pending QC ONLY for quantities
              that have already been returned to the customer as-is via dispatch. Fully-closed challans
              move to QC History as "Closed due to material returned to customer".
            </DialogDescription>
          </DialogHeader>
          {closeQCDlg && (
            <div className="space-y-1.5">
              <Label className="text-xs">Reason for closing</Label>
              <Textarea
                value={closeQCDlg.reason}
                onChange={(e) => setCloseQCDlg({ ...closeQCDlg, reason: e.target.value })}
                placeholder="e.g. Material returned to customer without processing on DC-xxxx"
                className="text-sm"
                rows={2}
                data-testid="close-qc-reason"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseQCDlg(null)} data-testid="close-qc-cancel">Cancel</Button>
            <Button onClick={submitCloseQC} disabled={closeQCSaving} data-testid="close-qc-confirm">
              {closeQCSaving ? 'Closing...' : 'Verify & Close QC'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={qcOpen} onOpenChange={setQcOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              {isOutboundQC ? 'Receive & QC' : 'Quality Check'}: {selectedChallan?.challan_number}
            </DialogTitle>
            <DialogDescription>
              {isOutboundQC 
                ? `Supplier: ${selectedChallan?.supplier_name}. Enter received quantities and assign QC disposition (Accept/Quarantine/Reject). You can receive partial quantities.`
                : `From: ${selectedChallan?.supplier_name}. Assign each item's quantity across Accepted (Stores), Quarantined, or Rejected.`
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={acceptAllItems} className="h-7 text-xs text-emerald-600 border-emerald-200" data-testid="qc-accept-all">
                <ShieldCheck className="w-3 h-3 mr-1" /> {isOutboundQC ? 'Receive & Accept All' : 'Accept All Items'}
              </Button>
            </div>

            {dispositions.map((d, idx) => {
              const accepted = parseFloat(d.accepted_qty) || 0;
              const quarantined = parseFloat(d.quarantined_qty) || 0;
              const rejected = parseFloat(d.rejected_qty) || 0;
              const received = isOutboundQC ? (parseFloat(d.received_qty) || 0) : (d.pending_qc_qty ?? d.total_qty);
              const qcTotal = accepted + quarantined + rejected;
              const remaining = received - qcTotal;
              const isItemDone = d.quality_status === 'qc_completed';

              return (
                <Card key={idx} className={`p-3 ${isItemDone ? 'opacity-60' : ''}`} data-testid={`qc-disposition-${idx}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium">{d.item_name}</p>
                      <div className="flex gap-2 flex-wrap">
                        <p className="text-[10px] text-muted-foreground font-mono">{d.item_unique_id}</p>
                        {d.traceability_id && (
                          <p className="text-[10px] text-blue-600 font-mono">Trace: {d.traceability_id}</p>
                        )}
                        {d.work_order_number && (
                          <p className="text-[10px] text-violet-600 font-mono">WO: {d.work_order_number}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {isOutboundQC ? (
                        <>
                          <Badge variant="outline" className="text-xs">Sent: {d.sent_qty}</Badge>
                          <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">Pending: {d.pending_receive_qty}</Badge>
                        </>
                      ) : (
                        <>
                          <Badge variant="outline" className="text-xs">Total: {d.total_qty}</Badge>
                          {d.already_inspected > 0 && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              Inspected: {d.already_inspected}
                            </Badge>
                          )}
                          <Badge variant="outline" className={`text-xs ${d.pending_qc_qty > 0 ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                            Pending: {d.pending_qc_qty ?? d.total_qty}
                          </Badge>
                        </>
                      )}
                      {d.quality_status === 'qc_completed' ? (
                        <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Done</Badge>
                      ) : d.quality_status === 'partially_inspected' ? (
                        <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">Partial</Badge>
                      ) : null}
                      {(d.pending_qc_qty ?? d.total_qty) > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => acceptAll(idx)} className="h-6 text-[10px] text-emerald-600" data-testid={`qc-accept-item-${idx}`}>
                          {isOutboundQC ? 'Receive All' : 'Accept All'}
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {/* Show previous inspection results if any */}
                  {!isOutboundQC && d.already_inspected > 0 && (
                    <div className="mb-2 p-2 bg-slate-50 rounded border border-slate-200 text-[10px]">
                      <span className="font-medium text-slate-600">Previous: </span>
                      <span className="text-emerald-600">Accepted: {d.prev_accepted}</span>
                      {d.prev_quarantined > 0 && <span className="ml-2 text-orange-600">Quarantined: {d.prev_quarantined}</span>}
                      {d.prev_rejected > 0 && <span className="ml-2 text-red-600">Rejected: {d.prev_rejected}</span>}
                    </div>
                  )}
                  
                  {isOutboundQC && (
                    <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[10px] text-blue-700 font-medium">Qty to Receive Now *</Label>
                          <Input
                            type="number" min="0" max={d.pending_receive_qty} step="any"
                            value={d.received_qty}
                            onChange={e => handleReceivedQtyChange(idx, e.target.value)}
                            className="h-8 text-xs border-blue-200 focus:ring-blue-500"
                            placeholder={`Max: ${d.pending_receive_qty}`}
                            data-testid={`qc-received-${idx}`}
                          />
                        </div>
                        <div className="flex items-end">
                          <p className="text-[10px] text-blue-600">
                            Already received: {d.already_received} | After this: {d.already_received + (parseFloat(d.received_qty) || 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-[10px] text-emerald-700 font-medium">Accepted (→ Stores)</Label>
                      <Input
                        type="number" min="0" max={received} step="any"
                        value={d.accepted_qty}
                        onChange={e => updateDisposition(idx, 'accepted_qty', e.target.value)}
                        className="h-8 text-xs border-emerald-200 focus:ring-emerald-500"
                        disabled={isItemDone}
                        data-testid={`qc-accepted-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-orange-700 font-medium">Quarantined (→ Quarantine)</Label>
                      <Input
                        type="number" min="0" max={received} step="any"
                        value={d.quarantined_qty}
                        onChange={e => updateDisposition(idx, 'quarantined_qty', e.target.value)}
                        className="h-8 text-xs border-orange-200 focus:ring-orange-500"
                        disabled={isItemDone}
                        data-testid={`qc-quarantined-${idx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-red-700 font-medium">Rejected (→ Rejection Area)</Label>
                      <Input
                        type="number" min="0" max={received} step="any"
                        value={d.rejected_qty}
                        onChange={e => updateDisposition(idx, 'rejected_qty', e.target.value)}
                        className="h-8 text-xs border-red-200 focus:ring-red-500"
                        disabled={isItemDone}
                        data-testid={`qc-rejected-${idx}`}
                      />
                    </div>
                  </div>
                  {/* Show current session unassigned count */}
                  {qcTotal > 0 && remaining !== 0 && (
                    <div className={`mt-2 text-[10px] ${remaining < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                      {remaining < 0 
                        ? `⚠ Over-assigned by ${Math.abs(remaining)}` 
                        : `${remaining} of ${received} pending QC qty still unassigned (can submit partial)`}
                    </div>
                  )}
                  <div className="mt-2">
                    <Input
                      placeholder="Item-level remarks (optional)"
                      value={d.remarks}
                      onChange={e => updateDisposition(idx, 'remarks', e.target.value)}
                      className="h-7 text-xs"
                      disabled={isItemDone}
                      data-testid={`qc-item-remarks-${idx}`}
                    />
                  </div>
                </Card>
              );
            })}

            <div>
              <Label className="text-xs">Overall Remarks</Label>
              <Textarea
                value={overallRemarks}
                onChange={e => setOverallRemarks(e.target.value)}
                placeholder="Overall QC remarks..."
                className="text-xs h-16"
                data-testid="qc-overall-remarks"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setQcOpen(false)} size="sm">Cancel</Button>
            <Button
              onClick={handleSubmitQC}
              disabled={submitting}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="qc-submit-decision"
            >
              {submitting && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              {isOutboundQC ? 'Receive & Process QC' : 'Submit QC Decision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt QC Decision Dialog */}
      <Dialog open={receiptQCOpen} onOpenChange={setReceiptQCOpen}>
        <DialogContent className="max-w-lg" data-testid="receipt-qc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-indigo-600" />
              Receipt QC: {selectedReceipt?.item_name}
            </DialogTitle>
            <DialogDescription>
              Inspect received material and assign disposition. Accepted items go to Stores, quarantined to Quarantine, rejected to Rejection Area.
            </DialogDescription>
          </DialogHeader>

          {selectedReceipt && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg border space-y-1 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Item:</span>
                  <span className="font-medium">{selectedReceipt.item_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reference:</span>
                  <span>{selectedReceipt.reference_number || '-'}</span>
                </div>
                {selectedReceipt.qc_supplier_name && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Supplier:</span>
                    <span>{selectedReceipt.qc_supplier_name}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Qty:</span>
                  <span className="font-medium">{selectedReceipt.quantity}</span>
                </div>
                {selectedReceipt.qc_inspected_qty > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Already Inspected:</span>
                    <span>{selectedReceipt.qc_inspected_qty} (Acc: {selectedReceipt.qc_accepted_qty || 0}, Q: {selectedReceipt.qc_quarantined_qty || 0}, R: {selectedReceipt.qc_rejected_qty || 0})</span>
                  </div>
                )}
                <div className="flex justify-between font-medium text-amber-700">
                  <span>Pending QC Qty:</span>
                  <span>{receiptDisposition.pending_qty}</span>
                </div>
              </div>

              <div className="flex justify-end">
                <Button size="sm" variant="ghost" onClick={receiptAcceptAll} className="h-7 text-xs text-emerald-600" data-testid="receipt-qc-accept-all">
                  Accept All ({receiptDisposition.pending_qty})
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10px] text-emerald-700 font-medium">Accepted (→ Stores)</Label>
                  <Input
                    type="number" min="0" step="any"
                    value={receiptDisposition.accepted_qty}
                    onChange={e => setReceiptDisposition(prev => ({ ...prev, accepted_qty: e.target.value }))}
                    className="h-8 text-xs border-emerald-200"
                    data-testid="receipt-qc-accepted"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-orange-700 font-medium">Quarantined</Label>
                  <Input
                    type="number" min="0" step="any"
                    value={receiptDisposition.quarantined_qty}
                    onChange={e => setReceiptDisposition(prev => ({ ...prev, quarantined_qty: e.target.value }))}
                    className="h-8 text-xs border-orange-200"
                    data-testid="receipt-qc-quarantined"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-red-700 font-medium">Rejected</Label>
                  <Input
                    type="number" min="0" step="any"
                    value={receiptDisposition.rejected_qty}
                    onChange={e => setReceiptDisposition(prev => ({ ...prev, rejected_qty: e.target.value }))}
                    className="h-8 text-xs border-red-200"
                    data-testid="receipt-qc-rejected"
                  />
                </div>
              </div>
              {(() => {
                const disposed = (parseFloat(receiptDisposition.accepted_qty) || 0) + (parseFloat(receiptDisposition.quarantined_qty) || 0) + (parseFloat(receiptDisposition.rejected_qty) || 0);
                const remaining = receiptDisposition.pending_qty - disposed;
                if (disposed > 0 && remaining !== 0) {
                  return <p className={`text-[10px] ${remaining < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                    {remaining < 0 ? `Over-assigned by ${Math.abs(remaining)}` : `${remaining} still unassigned (can submit partial)`}
                  </p>;
                }
                return null;
              })()}

              <div>
                <Label className="text-xs">Remarks</Label>
                <Input
                  value={receiptDisposition.remarks}
                  onChange={e => setReceiptDisposition(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="QC remarks..."
                  className="h-8 text-xs"
                  data-testid="receipt-qc-remarks"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptQCOpen(false)} size="sm">Cancel</Button>
            <Button
              onClick={handleSubmitReceiptQC}
              disabled={submittingReceipt}
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="receipt-qc-submit"
            >
              {submittingReceipt && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
              Submit QC Decision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin: wrong item booked on a pending receipt */}
      <Dialog open={!!correctTarget} onOpenChange={o => !o && setCorrectTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="receipt-correct-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">Receipt booked against the wrong item?</DialogTitle>
            <DialogDescription className="text-xs">
              Moves {correctTarget?.txn?.quantity} {correctTarget?.txn?.primary_uom || ''} of{' '}
              <strong>{correctTarget?.txn?.item_unique_id}</strong> in {correctTarget?.txn?.godown_name} to the correct
              item. The receipt, its trace lot and the QC queue entry all move with it — nothing else to reconcile.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Correct item *</Label>
              <SearchableSelect
                options={fixItems.filter(i => i.id !== correctTarget?.txn?.item_id).map(i => ({
                  value: i.id, label: `${i.unique_id} - ${i.item_name}`,
                  sub: `${i.item_type || ''}${i.category ? ' | ' + i.category : ''}`,
                  searchStr: `${i.unique_id} ${i.item_name} ${i.external_id || ''}`,
                }))}
                value={correctTarget?.item_id || ''}
                onSelect={v => setCorrectTarget(t => ({ ...t, item_id: v }))}
                placeholder="Search the correct item..." emptyText="No items found"
                testId="receipt-correct-item-select"
              />
            </div>
            <div>
              <Label className="text-xs">Reason * (audit log)</Label>
              <Input value={correctTarget?.reason || ''}
                onChange={e => setCorrectTarget(t => ({ ...t, reason: e.target.value }))}
                placeholder="e.g. GRN taken on wrong RM number"
                className="h-8 text-xs" data-testid="receipt-correct-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCorrectTarget(null)}>Cancel</Button>
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" disabled={fixBusy}
              onClick={submitItemCorrection} data-testid="receipt-correct-submit">
              {fixBusy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Move to correct item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin: void a wrong pending receipt */}
      <Dialog open={!!voidTarget} onOpenChange={o => !o && setVoidTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="receipt-void-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm">Void this receipt?</DialogTitle>
            <DialogDescription className="text-xs">
              Cancels the receipt of {voidTarget?.txn?.quantity} {voidTarget?.txn?.primary_uom || ''}{' '}
              <strong>{voidTarget?.txn?.item_unique_id}</strong> and removes it from the QC queue. The stock is
              reversed out of {voidTarget?.txn?.godown_name} unless you have already adjusted it manually.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={!!voidTarget?.stock_already_adjusted}
                onChange={e => setVoidTarget(t => ({ ...t, stock_already_adjusted: e.target.checked }))}
                className="h-3.5 w-3.5 accent-slate-600" data-testid="receipt-void-already-adjusted" />
              I have already corrected the quantity manually — do not touch stock
            </label>
            <div>
              <Label className="text-xs">Reason * (audit log)</Label>
              <Input value={voidTarget?.reason || ''}
                onChange={e => setVoidTarget(t => ({ ...t, reason: e.target.value }))}
                placeholder="e.g. duplicate GRN on wrong RM, corrected manually"
                className="h-8 text-xs" data-testid="receipt-void-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setVoidTarget(null)}>Cancel</Button>
            <Button size="sm" className="bg-rose-600 hover:bg-rose-700" disabled={fixBusy}
              onClick={submitVoidReceipt} data-testid="receipt-void-submit">
              {fixBusy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Void receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
